import { useState, useEffect, useCallback, useRef } from 'react';

interface TransactionStatus {
  id: string;
  status: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number | string;
  toAmount: number | string;
  payoutAddress: string;
  payinAddress: string;
  createdAt: string;
  updatedAt?: string;
  depositReceived?: boolean;
  payoutSent?: boolean;
  transactions?: any[];
  changeNowData?: any;
}

interface UseTransactionStatusOptions {
  refreshInterval?: number;
  autoRefresh?: boolean;
  maxRetries?: number;
  demoMode?: boolean;
}

const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';

export const useTransactionStatus = (
  transactionId: string | null,
  options: UseTransactionStatusOptions = {}
) => {
  const {
    refreshInterval = 10000,
    autoRefresh = true,
    demoMode = false // Use real backend data
  } = options;

  const [status, setStatus] = useState<TransactionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(autoRefresh);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const activeRequestRef = useRef<AbortController | null>(null);
  const statusRef = useRef<TransactionStatus | null>(null);

  // Keep statusRef in sync
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const fetchStatus = useCallback(async (
    skipCache: boolean = false
  ): Promise<void> => {
    if (!transactionId) {
      return;
    }

    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    activeRequestRef.current = new AbortController();
    setIsLoading(true);
    setError(null);

    try {
      // Demo mode - return mock status
      if (demoMode) {
        await new Promise(resolve => setTimeout(resolve, 500));

        // Only fetch if we don't have status yet
        if (!statusRef.current) {
          const mockStatus: TransactionStatus = {
            id: transactionId,
            status: 'waiting',
            fromCurrency: 'btc',
            toCurrency: 'usdt_eth',
            fromAmount: '0.01',
            toAmount: '950',
            payoutAddress: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb',
            payinAddress: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
            createdAt: new Date().toISOString(),
            depositReceived: false,
            payoutSent: false,
          };
          setStatus(mockStatus);
        }

        setIsLoading(false);
        return;
      }

      // Real API call
      const response = await fetch(`${API_URL}/status/${transactionId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: activeRequestRef.current.signal
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStatus(data.data);
          setRetryCount(0);

          const normalizedStatus = (data.data.status || '').toLowerCase();
          if (['finished', 'completed', 'done', 'failed'].includes(normalizedStatus)) {
            setIsAutoRefreshEnabled(false);
          }
          setIsLoading(false);
          return;
        }
      }

      throw new Error('Status request failed');

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      setIsLoading(false);

      // In demo mode, don't show errors
      if (!demoMode) {
        setError('Failed to fetch status');
      }
    }
  }, [transactionId, demoMode]);

  const refreshStatus = useCallback(() => {
    fetchStatus(true);
  }, [fetchStatus]);

  const toggleAutoRefresh = useCallback((enabled: boolean) => {
    setIsAutoRefreshEnabled(enabled);
    if (enabled && transactionId) {
      fetchStatus(true);
    }
  }, [fetchStatus, transactionId]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (isAutoRefreshEnabled && transactionId) {
      fetchStatus(true);

      intervalRef.current = setInterval(() => {
        fetchStatus();
      }, refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoRefreshEnabled, transactionId, refreshInterval, fetchStatus]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
      }
    };
  }, []);

  const normalizedStatus = status ? (status.status || '').toLowerCase() : '';
  const isPending = status && ['new', 'waiting', 'confirming', 'exchanging', 'sending'].includes(normalizedStatus);
  const isCompleted = status && ['finished', 'completed', 'done'].includes(normalizedStatus);
  const isFailed = status && normalizedStatus === 'failed';
  const needsDeposit = status && !status.depositReceived && ['new', 'waiting'].includes(normalizedStatus);

  return {
    status,
    isLoading,
    error,
    retryCount,
    isAutoRefreshEnabled,
    refreshStatus,
    toggleAutoRefresh,
    isPending,
    isCompleted,
    isFailed,
    needsDeposit
  };
};
